import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Link as MuiLink,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { PercentOutlined as PercentOutlinedIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { hostApiClient } from '../../hostApiClient';
import {
  CommissionChannelFilter,
  CommissionCharge,
  CommissionChargesResponse,
  CommissionSettlementStatus,
  formatCommissionAmount,
} from '../../models/Subscription';
import { roomBookingDetailsPath } from '../../utils/roomBookingDetailsPath';
import { tutorBookingDetailsPath } from '../../utils/tutorBookingDetailsPath';
import LedgerPagination from '../common/LedgerPagination';
import {
  DEFAULT_LEDGER_ROWS_PER_PAGE,
} from '../common/LedgerRowsPerPage';

const CHANNEL_FILTERS: CommissionChannelFilter[] = ['ALL', 'ONLINE', 'CASH'];

function settlementStatusColor(
  status: CommissionSettlementStatus,
): 'success' | 'warning' | 'info' | 'default' {
  switch (status) {
    case 'COLLECTED':
      return 'success';
    case 'PENDING_INVOICE':
    case 'PARTIALLY_REVERSED':
    case 'CREDIT_PENDING':
      return 'warning';
    case 'INVOICED':
      return 'info';
    case 'REVERSED':
    case 'VOIDED':
    case 'CREDITED':
    default:
      return 'default';
  }
}

/** True when the row should show original / reversed / net breakdown. */
function hasCommissionReversal(charge: CommissionCharge): boolean {
  return charge.commissionReversedAmount > 0;
}

function bookingPath(charge: CommissionCharge): string {
  return charge.sourceType === 'ROOM_BOOKING'
    ? roomBookingDetailsPath('host', charge.sourceBookingId)
    : tutorBookingDetailsPath('host', charge.sourceBookingId);
}

function chargeRowKey(charge: CommissionCharge): string {
  return [
    charge.channel,
    charge.sourceBookingId,
    charge.collectedAt,
    charge.stripeReferenceId ?? '',
    charge.commissionAmount,
  ].join(':');
}

/**
 * Online + cash commission ledger on Plan and Billing.
 * Aesthetic: calm operations ledger with channel filters — cash INVOICED is not “paid”.
 */
const CommissionChargesPanel: React.FC = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();

  const [channel, setChannel] = useState<CommissionChannelFilter>('ALL');
  const [data, setData] = useState<CommissionChargesResponse | null>(null);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_LEDGER_ROWS_PER_PAGE);
  const [pageIndex, setPageIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (nextChannel: CommissionChannelFilter) => {
    try {
      setLoading(true);
      setError(null);
      const response = await hostApiClient.getCommissionCharges({ channel: nextChannel });
      setData(response);
      setPageIndex(0);
    } catch (err) {
      console.error('Error loading commission charges:', err);
      setError(
        t('subscription.commissionCharges.fetchError', 'Failed to load commission charges'),
      );
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load(channel);
  }, [channel, load]);

  const handleRowsPerPageChange = (next: number) => {
    setRowsPerPage(next);
    setPageIndex(0);
  };

  const formatDateTime = (value: string | null | undefined) => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleString(i18n.language, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const currency = data?.currencyCode ?? 'PLN';
  const money = (amount: number) =>
    formatCommissionAmount(amount, currency, i18n.language);
  const totalItems = data?.items.length ?? 0;
  const pageCount = Math.max(1, Math.ceil(totalItems / rowsPerPage));
  const safePageIndex = Math.min(pageIndex, pageCount - 1);
  const pageStart = safePageIndex * rowsPerPage;
  const visibleItems = data?.items.slice(pageStart, pageStart + rowsPerPage) ?? [];
  const rangeFrom = totalItems === 0 ? 0 : pageStart + 1;
  const rangeTo = Math.min(pageStart + rowsPerPage, totalItems);

  return (
    <Box
      sx={{
        borderRadius: 2,
        border: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
        overflow: 'hidden',
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.25}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
        sx={{
          px: { xs: 2, sm: 2.5 },
          py: 1.75,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: alpha(theme.palette.primary.main, 0.04),
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1,
              display: 'grid',
              placeItems: 'center',
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              flexShrink: 0,
            }}
          >
            <PercentOutlinedIcon sx={{ fontSize: 18, color: 'primary.main' }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              component="h2"
              fontWeight={800}
              sx={{ letterSpacing: '-0.01em' }}
            >
              {t('subscription.commissionCharges.title', 'Commission charges')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t(
                'subscription.commissionCharges.subtitle',
                'Platform commission from online and cash bookings. Cash invoiced items are queued for the next subscription invoice — not proof of payment.',
              )}
            </Typography>
          </Box>
        </Stack>

        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ flexShrink: 0, alignSelf: { xs: 'flex-start', sm: 'center' } }}
        >
          <ToggleButtonGroup
            exclusive
            size="small"
            value={channel}
            onChange={(_event, value: CommissionChannelFilter | null) => {
              if (value != null) setChannel(value);
            }}
            aria-label={t(
              'subscription.commissionCharges.filtersLabel',
              'Filter by payment channel',
            )}
            sx={{
              flexWrap: 'wrap',
              '& .MuiToggleButtonGroup-grouped': {
                border: 1,
                borderColor: 'divider',
                px: 1.5,
                py: 0.5,
                textTransform: 'none',
                fontWeight: 700,
                '&.Mui-selected': {
                  bgcolor: alpha(theme.palette.primary.main, 0.16),
                  borderColor: alpha(theme.palette.primary.main, 0.4),
                  color: 'primary.light',
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.22),
                  },
                },
              },
            }}
          >
            {CHANNEL_FILTERS.map((filter) => (
              <ToggleButton key={filter} value={filter}>
                {t(`subscription.commissionCharges.filters.${filter}`, filter)}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          {loading && data ? <CircularProgress size={16} /> : null}
        </Stack>
      </Stack>

      {loading && !data && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={140} px={2}>
          <CircularProgress size={28} />
        </Box>
      )}

      {!loading && error && !data && (
        <Box sx={{ p: 2 }}>
          <Alert
            severity="error"
            sx={{ borderRadius: 2 }}
            action={
              <Button color="inherit" size="small" onClick={() => void load(channel)}>
                {t('subscription.commissionCharges.retry', 'Retry')}
              </Button>
            }
          >
            {error}
          </Alert>
        </Box>
      )}

      {data && (
        <>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={{ xs: 1.25, sm: 2 }}
            divider={
              <Divider
                orientation="vertical"
                flexItem
                sx={{ display: { xs: 'none', sm: 'block' } }}
              />
            }
            sx={{
              px: { xs: 2, sm: 2.5 },
              py: 2,
              borderBottom: 1,
              borderColor: 'divider',
              bgcolor: alpha(theme.palette.background.default, 0.5),
            }}
          >
            <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" color="text.secondary">
                {t(
                  'subscription.commissionCharges.summary.onlineCollected',
                  'Online collected',
                )}
              </Typography>
              <Typography variant="subtitle1" fontWeight={800} sx={{ letterSpacing: '-0.02em' }}>
                {money(data.onlineCollectedAmount)}
              </Typography>
            </Stack>
            <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" color="text.secondary">
                {t(
                  'subscription.commissionCharges.summary.cashPending',
                  'Cash waiting for invoice',
                )}
              </Typography>
              <Typography variant="subtitle1" fontWeight={800} sx={{ letterSpacing: '-0.02em' }}>
                {money(data.cashPendingAmount)}
              </Typography>
            </Stack>
            <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" color="text.secondary">
                {t(
                  'subscription.commissionCharges.summary.cashInvoiced',
                  'Cash queued on invoice',
                )}
              </Typography>
              <Typography variant="subtitle1" fontWeight={800} sx={{ letterSpacing: '-0.02em' }}>
                {money(data.cashInvoicedAmount)}
              </Typography>
            </Stack>
          </Stack>

          {error && (
            <Box sx={{ px: { xs: 2, sm: 2.5 }, pt: 1.5 }}>
              <Alert
                severity="error"
                sx={{ borderRadius: 2 }}
                action={
                  <Button color="inherit" size="small" onClick={() => void load(channel)}>
                    {t('subscription.commissionCharges.retry', 'Retry')}
                  </Button>
                }
              >
                {error}
              </Alert>
            </Box>
          )}

          {!error && data.items.length === 0 ? (
            <Box sx={{ px: { xs: 2, sm: 2.5 }, py: 3 }}>
              <Typography variant="body2" color="text.secondary">
                {t(
                  `subscription.commissionCharges.empty.${channel}`,
                  channel === 'ONLINE'
                    ? 'No online commission charges yet.'
                    : channel === 'CASH'
                      ? 'No cash commission charges yet.'
                      : 'No commission charges yet.',
                )}
              </Typography>
            </Box>
          ) : !error ? (
            <>
              <Stack
                divider={<Divider flexItem />}
                sx={{
                  pb: 0,
                  opacity: loading ? 0.55 : 1,
                  transition: 'opacity 120ms ease',
                  pointerEvents: loading ? 'none' : 'auto',
                }}
                aria-busy={loading}
              >
                {visibleItems.map((charge) => {
                const collectedLabel = formatDateTime(charge.collectedAt);
                const rowCurrency = charge.currencyCode || currency;
                const showReversal = hasCommissionReversal(charge);
                const netLabel = formatCommissionAmount(
                  charge.netCommissionAmount,
                  rowCurrency,
                  i18n.language,
                );
                const originalLabel = formatCommissionAmount(
                  charge.commissionAmount,
                  rowCurrency,
                  i18n.language,
                );
                const reversedLabel = formatCommissionAmount(
                  charge.commissionReversedAmount,
                  rowCurrency,
                  i18n.language,
                );
                const sourceLabel = t(
                  `subscription.commissionCharges.sourceType.${charge.sourceType}`,
                  charge.sourceType === 'ROOM_BOOKING' ? 'Room booking' : 'Tutor booking',
                );
                const channelLabel = t(
                  `subscription.commissionCharges.channel.${charge.channel}`,
                  charge.channel === 'ONLINE' ? 'Online' : 'Cash',
                );
                const metaParts: string[] = [];
                if (charge.bookingAmount != null) {
                  metaParts.push(
                    t(
                      'subscription.commissionCharges.bookingAmount',
                      'Booking {{amount}}',
                      {
                        amount: formatCommissionAmount(
                          charge.bookingAmount,
                          rowCurrency,
                          i18n.language,
                        ),
                      },
                    ),
                  );
                }
                if (charge.commissionPercent != null) {
                  metaParts.push(
                    t(
                      'subscription.commissionCharges.commissionPercent',
                      '{{percent}}% commission',
                      {
                        percent: new Intl.NumberFormat(i18n.language, {
                          maximumFractionDigits: 2,
                        }).format(charge.commissionPercent),
                      },
                    ),
                  );
                }
                if (charge.stripeProcessingFeeAmount != null) {
                  metaParts.push(
                    t(
                      'subscription.commissionCharges.stripeProcessingFee',
                      'Stripe processing fee {{amount}}',
                      {
                        amount: formatCommissionAmount(
                          charge.stripeProcessingFeeAmount,
                          charge.stripeProcessingFeeCurrency || rowCurrency,
                          i18n.language,
                        ),
                      },
                    ),
                  );
                } else if (charge.channel === 'ONLINE') {
                  metaParts.push(
                    t(
                      'subscription.commissionCharges.stripeProcessingFeeUnavailable',
                      'Stripe processing fee not available',
                    ),
                  );
                }
                if (showReversal) {
                  metaParts.push(
                    t(
                      'subscription.commissionCharges.originalAmount',
                      'Original {{amount}}',
                      { amount: originalLabel },
                    ),
                  );
                  metaParts.push(
                    t(
                      'subscription.commissionCharges.reversedAmount',
                      'Reversed {{amount}}',
                      { amount: reversedLabel },
                    ),
                  );
                }
                if (collectedLabel) {
                  metaParts.push(
                    t(
                      'subscription.commissionCharges.collectedAt',
                      'Collected {{date}}',
                      { date: collectedLabel },
                    ),
                  );
                }

                return (
                  <Stack
                    key={chargeRowKey(charge)}
                    direction="row"
                    spacing={{ xs: 1.25, sm: 1.75 }}
                    alignItems="center"
                    sx={{ px: { xs: 2, sm: 2.5 }, py: 1.75 }}
                  >
                    <Chip
                      label={t(
                        `subscription.commissionCharges.status.${charge.settlementStatus}`,
                        charge.settlementStatus,
                      )}
                      color={settlementStatusColor(charge.settlementStatus)}
                      sx={{
                        fontWeight: 800,
                        fontSize: '0.8125rem',
                        height: 32,
                        px: 0.5,
                        flexShrink: 0,
                        '& .MuiChip-label': { px: 1.25 },
                      }}
                    />

                    <Stack spacing={0.25} sx={{ minWidth: 0, flex: 1 }}>
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{ minWidth: 0, flexWrap: 'wrap' }}
                      >
                        <MuiLink
                          component={RouterLink}
                          to={bookingPath(charge)}
                          underline="hover"
                          color="inherit"
                          sx={{ fontWeight: 700, typography: 'body1' }}
                        >
                          {sourceLabel}
                        </MuiLink>
                        {channel === 'ALL' && (
                          <Chip
                            size="small"
                            label={channelLabel}
                            variant="outlined"
                            sx={{ height: 22, fontWeight: 700 }}
                          />
                        )}
                      </Stack>
                      {metaParts.length > 0 && (
                        <Typography variant="body2" color="text.secondary">
                          {metaParts.join(' · ')}
                        </Typography>
                      )}
                      {charge.stripeReceiptUrl?.trim() ? (
                        <MuiLink
                          href={charge.stripeReceiptUrl.trim()}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="body2"
                          underline="hover"
                          sx={{ fontWeight: 600, width: 'fit-content' }}
                        >
                          {t(
                            'subscription.commissionCharges.viewStripeReceipt',
                            'View Receipt',
                          )}
                        </MuiLink>
                      ) : null}
                    </Stack>

                    <Stack
                      spacing={0}
                      alignItems="flex-end"
                      sx={{ flexShrink: 0, textAlign: 'right' }}
                    >
                      <Typography
                        variant="body1"
                        fontWeight={800}
                        sx={{ letterSpacing: '-0.01em' }}
                      >
                        {netLabel}
                      </Typography>
                      {showReversal && (
                        <Typography variant="caption" color="text.secondary">
                          {t(
                            'subscription.commissionCharges.netAmountLabel',
                            'Net retained',
                          )}
                        </Typography>
                      )}
                    </Stack>
                  </Stack>
                );
                })}
              </Stack>
              <Box
                sx={{
                  px: { xs: 2, sm: 2.5 },
                  py: 1.5,
                  borderTop: 1,
                  borderColor: 'divider',
                }}
              >
                <LedgerPagination
                  id="commission-charges"
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleRowsPerPageChange}
                  canGoPrevious={safePageIndex > 0}
                  canGoNext={safePageIndex < pageCount - 1}
                  onPrevious={() => setPageIndex((index) => Math.max(0, index - 1))}
                  onNext={() =>
                    setPageIndex((index) => Math.min(pageCount - 1, index + 1))
                  }
                  disabled={loading}
                  label={t(
                    'common.pagination.displayedRows',
                    '{{from}}–{{to}} of {{count}}',
                    { from: rangeFrom, to: rangeTo, count: totalItems },
                  )}
                />
              </Box>
            </>
          ) : null}
        </>
      )}
    </Box>
  );
};

export default CommissionChargesPanel;
